import { formatMoney } from '../../utils/detectCountry';
import apiClient from './apiClient';

export const CATALOG_PLAN_SLUGS = ['free', 'player', 'pro', 'elite', 'legend'] as const;
export type CatalogPlanSlug = (typeof CATALOG_PLAN_SLUGS)[number];

export interface PaymentCatalogCountry {
  code: string;
  name: string;
  currency: string;
  locale: string;
  phone_prefix: string;
  enabled: boolean;
}

export interface PaymentCatalogMethod {
  code: string;
  label: string;
}

export interface PaymentCatalogProvider {
  name: string;
  display_name: string;
  methods: PaymentCatalogMethod[];
}

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;
export type PlanEntitlementValueKind = 'boolean' | 'quota';
export type PlanEntitlementPeriod = 'none' | 'month' | 'year';

export interface PlanEntitlement {
  key: string;
  name: string;
  value_kind: PlanEntitlementValueKind;
  period: PlanEntitlementPeriod;
  enabled: boolean;
  limit?: number | null;
}

export interface LocalizedAppPlan {
  id?: string;
  slug: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  price_clp?: number;
  price_usd?: number;
  prices?: Record<string, number>;
  duration_days: number;
  features: string[] | unknown;
  entitlements?: PlanEntitlement[];
  is_popular: boolean;
  is_active?: boolean;
}

export interface PaymentCatalog {
  country: PaymentCatalogCountry;
  requested_country: string;
  fallback: boolean;
  providers: PaymentCatalogProvider[];
  app_plans: LocalizedAppPlan[];
}

export async function getPaymentCatalog(country?: string): Promise<PaymentCatalog> {
  const qs = country ? `?country=${encodeURIComponent(country)}` : '';
  const { data } = await apiClient.get<PaymentCatalog>(`/public/payments/catalog${qs}`, {
    skipAuth: true,
  });
  return data;
}

export function isPlanActive(plan: LocalizedAppPlan): boolean {
  return plan.is_active !== false;
}

export function selectActiveCatalogPlans(plans: LocalizedAppPlan[] | undefined | null): LocalizedAppPlan[] {
  if (!plans?.length) return [];
  const active = plans.filter(isPlanActive);
  const bySlug = new Map(active.map((p) => [p.slug, p]));
  const preferred = CATALOG_PLAN_SLUGS.map((slug) => bySlug.get(slug)).filter(
    (p): p is LocalizedAppPlan => Boolean(p),
  );
  return preferred.length > 0 ? preferred : active;
}

export function featureStrings(features: string[] | unknown): string[] {
  if (!Array.isArray(features)) return [];
  return features.filter((f): f is string => typeof f === 'string' && f.trim().length > 0);
}

export function entitlementChecklist(
  entitlements: PlanEntitlement[] | undefined,
  t: TranslateFn,
): string[] {
  if (!entitlements?.length) return [];

  const lines: string[] = [];
  for (const item of entitlements) {
    const label = t(`entitlements.keys.${item.key}`, {
      defaultValue: item.name || item.key,
    });

    if (item.value_kind === 'quota') {
      const unlimited = item.enabled && (item.limit == null || item.limit < 0);
      if (!unlimited && (!item.enabled || !item.limit)) {
        continue;
      }
      if (unlimited) {
        lines.push(label);
        continue;
      }
      const count = item.limit ?? 0;
      const quotaKey = `entitlements.quota.${item.key}`;
      const translated = t(quotaKey, { count, defaultValue: '' });
      const base =
        translated && translated !== quotaKey
          ? translated
          : t('entitlements.quotaLine', { count, label, defaultValue: `${count} ${label}` });
      if (item.period === 'month' || item.period === 'year') {
        lines.push(`${base} (${t(`entitlements.period.${item.period}`)})`);
      } else {
        lines.push(base);
      }
      continue;
    }

    if (item.enabled) {
      lines.push(label);
    }
  }
  return lines;
}

export function planChecklist(plan: LocalizedAppPlan, t: TranslateFn): string[] {
  const fromEntitlements = entitlementChecklist(plan.entitlements, t);
  if (fromEntitlements.length) return fromEntitlements;
  return featureStrings(plan.features);
}

export function formatDurationDays(days: number, t: TranslateFn): string {
  if (days >= 28 && days % 30 === 0) {
    const months = days / 30;
    return `${months} ${t('pricing.months')}`;
  }
  if (days >= 28 && days % 28 === 0) {
    const months = days / 28;
    return `${months} ${t('pricing.months')}`;
  }
  return `${days} ${t('pricing.days')}`;
}

export function formatCatalogPlanPrice(plan: LocalizedAppPlan, useClp: boolean, t: TranslateFn): string {
  const clp = plan.price_clp ?? (plan.currency === 'CLP' ? plan.price : undefined);
  const usd = plan.price_usd ?? (plan.currency === 'USD' ? plan.price : undefined);
  const amount = useClp ? clp ?? plan.price : usd ?? plan.price;
  if (!amount) return t('pricing.free');

  if (useClp) {
    return formatMoney(amount, 'CLP', 'es-CL');
  }

  const currency = plan.currency && plan.currency !== 'CLP' ? plan.currency : 'USD';
  return formatMoney(amount, currency, currency === 'USD' ? 'en-US' : undefined);
}

export function clubApplicationLimit(plan: LocalizedAppPlan): number | undefined {
  const row = plan.entitlements?.find((e) => e.key === 'club_applications');
  if (row?.limit != null && row.limit > 0) return row.limit;
  return undefined;
}
